import { supabase } from "@/integrations/supabase/client";
import { clearDeviceTrust } from "@/lib/trustedDevice";

/**
 * Shared sign-in entry helpers.
 *
 * Two jobs only:
 *  - decide whether an intended destination is a safe internal path;
 *  - actually end the current session before offering another account.
 *
 * Neither widens any permission: a destination is only used after the role
 * check has already authorised the person.
 */

/** Paths that would bounce a just-signed-in person straight back out. */
const BLOCKED_ENTRY_PATHS = new Set([
  "/portal/login",
  "/customer/login",
  "/admin/login",
  "/reset-password",
  "/customer/reset-password",
  "/customer/set-password",
  "/account/password-reset",
]);

export interface SafeReturnOptions {
  /** Only paths under one of these prefixes are accepted. */
  allowedPrefixes: string[];
  /** Used whenever the candidate is missing, external or blocked. */
  fallback: string;
}

/**
 * Accepts same-origin relative destinations only. Absolute URLs,
 * protocol-relative URLs, javascript: targets and unrelated areas of the
 * platform are all rejected in favour of the fallback.
 */
export function safeInternalReturnPath(
  candidate: string | null | undefined,
  { allowedPrefixes, fallback }: SafeReturnOptions,
): string {
  if (!candidate) return fallback;
  let decoded: string;
  try {
    decoded = decodeURIComponent(candidate);
  } catch {
    return fallback;
  }
  if (!decoded.startsWith("/") || decoded.startsWith("//")) return fallback;
  // eslint-disable-next-line no-control-regex -- deliberate: rejects control characters in untrusted input
  if (decoded.includes("\\") || /[\u0000-\u001f]/.test(decoded)) return fallback;

  let url: URL;
  try {
    url = new URL(decoded, "https://local.invalid");
  } catch {
    return fallback;
  }
  if (url.origin !== "https://local.invalid") return fallback;
  if (BLOCKED_ENTRY_PATHS.has(url.pathname)) return fallback;

  const allowed = allowedPrefixes.some(
    (prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
  );
  if (!allowed) return fallback;

  return `${url.pathname}${url.search}${url.hash}`;
}

/** Approval screen used when an external assistant connects to this app. */
export const OAUTH_CONSENT_PATH = "/.lovable/oauth/consent";

/** Safe destination for the professional portal. */
export function safePortalReturnPath(candidate: string | null | undefined): string {
  return safeInternalReturnPath(candidate, {
    allowedPrefixes: ["/portal", OAUTH_CONSENT_PATH],
    fallback: "/portal",
  });
}

export interface SignOutOutcome {
  ok: boolean;
  /** Present only when the sign-out did not complete. */
  message?: string;
}

/**
 * Ends the current session. Used before offering a different account, so a
 * failure must be reported honestly rather than navigating as if it worked.
 */
export async function endCurrentSession(): Promise<SignOutOutcome> {
  clearDeviceTrust();
  try {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) {
      return { ok: false, message: error.message || "You are still signed in." };
    }
    return { ok: true };
  } catch (caught) {
    return {
      ok: false,
      message: caught instanceof Error ? caught.message : "You are still signed in.",
    };
  }
}
